import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { User as FirebaseUser } from 'firebase/auth'
import { collection, deleteDoc, doc, onSnapshot, orderBy, query, updateDoc } from 'firebase/firestore'
import { CheckCircle, MoreHorizontal, Search, Shield, Trash2, User, UserX, XCircle } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext' // Fix import path
import FullPageLoader from '../components/ui/FullPageLoader' // Fix import path
import { Modal } from '../components/ui/Modal' // Fix import path
import { usePageTitle } from '../hooks/usePageTitle'
import { db, getFirestore } from '../services/firebase' // Fix import path
import { UserRole, UserStatus } from '../types/index' // Fix import path

// Types
interface UserData {
    uid: string
    displayName: string | null
    email: string | null
    photoURL: string | null
    role: UserRole
    status: UserStatus
    createdAt?: any
    lastLogin?: any
}

export const Route = createFileRoute('/_auth/users')({
    component: UsersRoute,
})

function UsersRoute() {
    usePageTitle('User Management')
    const { userData: user, isAdmin, loading } = useAuth()
    const navigate = useNavigate()
    const [users, setUsers] = useState<UserData[]>([])
    const [searchTerm, setSearchTerm] = useState('')
    const [isLoading, setIsLoading] = useState(true)
    const [selectedUser, setSelectedUser] = useState<UserData | null>(null)
    const [isActionModalOpen, setIsActionModalOpen] = useState(false)
    const [actionType, setActionType] = useState<'approve' | 'reject' | 'delete' | null>(null)

    // Redirect if not admin
    useEffect(() => {
        if (!loading && !isAdmin) {
            navigate({ to: '/dashboard' })
        }
    }, [isAdmin, loading, navigate])

    // Fetch Users (Realtime)
    useEffect(() => {
        if (!isAdmin) return

        // Use getter to ensure DB is initialized
        const firestore = db || getFirestore()
        if (!firestore) {
            console.error('Firestore not initialized')
            setIsLoading(false)
            return
        }

        const q = query(collection(firestore, 'users'), orderBy('createdAt', 'desc'))
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const fetchedUsers: UserData[] = []
                snapshot.forEach((doc) => {
                    fetchedUsers.push({
                        uid: doc.id,
                        ...doc.data(),
                    } as UserData)
                })
                setUsers(fetchedUsers)
                setIsLoading(false)
            },
            (error) => {
                console.error('Error fetching users:', error)
                setIsLoading(false)
                // Optionally show an error toast here
            },
        )

        return () => unsubscribe()
    }, [isAdmin])

    // Actions
    const handleUpdateStatus = async (uid: string, status: UserStatus) => {
        if (!db) return
        try {
            await updateDoc(doc(db, 'users', uid), { status })
            setIsActionModalOpen(false)
            setSelectedUser(null)
        } catch (error) {
            console.error('Error updating user status:', error)
            alert('Failed to update status')
        }
    }

    const handleDeleteUser = async (uid: string) => {
        if (!db) return
        try {
            await deleteDoc(doc(db, 'users', uid))
            setIsActionModalOpen(false)
            setSelectedUser(null)
        } catch (error) {
            console.error('Error deleting user:', error)
            alert('Failed to delete user')
        }
    }

    // Derived
    const filteredUsers = users.filter(
        (u) =>
            u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            u.displayName?.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    const pendingCount = users.filter((u) => u.status === UserStatus.PENDING).length

    if (loading || (isAdmin && isLoading)) {
        return <FullPageLoader message="Loading User Management..." />
    }

    if (!isAdmin) return null // Should redirect via useEffect

    return (
        <div className="space-y-6 pb-20 animate-in fade-in duration-500 max-w-7xl mx-auto p-6">
            {/* Premium Header Card */}
            <div className="relative overflow-hidden rounded-[32px] bg-white/80 dark:bg-slate-900/80 backdrop-blur-2xl border border-white/50 dark:border-slate-800/50 shadow-2xl shadow-indigo-500/5 p-8 mb-8">
                {/* Decorative Gradients */}
                <div className="absolute top-0 right-0 -mr-20 -mt-20 w-72 h-72 rounded-full bg-indigo-500/10 dark:bg-indigo-500/20 blur-3xl" />
                <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-72 h-72 rounded-full bg-rose-500/10 dark:bg-rose-500/20 blur-3xl" />
                
                <div className="absolute top-0 right-8 h-full flex items-center opacity-[0.03] dark:opacity-5 pointer-events-none">
                    <Shield size={200} className="rotate-12" />
                </div>

                <div className="relative z-10">
                    <div className="inline-flex items-center gap-3 px-4 py-2 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-bold text-sm mb-4 border border-indigo-100/50 dark:border-indigo-500/20">
                        <Shield size={18} />
                        <span>Security & Access</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black text-slate-800 dark:text-white tracking-tight mb-4">
                        User <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-rose-500">Management</span>
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 text-lg font-medium max-w-xl">
                        Monitor active sessions, manage roles, and securely approve new access requests in real-time.
                    </p>

                    <div className="flex flex-wrap gap-4 mt-8">
                        <div className="px-6 py-4 bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-100 dark:border-slate-700/50 shadow-sm flex items-center gap-4 group hover:-translate-y-1 transition-all duration-300">
                            <div className="p-3 bg-indigo-50 dark:bg-indigo-500/10 rounded-xl text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform duration-300">
                                <User size={24} strokeWidth={2.5} />
                            </div>
                            <div>
                                <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                                    Total Users
                                </p>
                                <p className="text-2xl font-black text-slate-800 dark:text-white leading-none">
                                    {users.length}
                                </p>
                            </div>
                        </div>

                        <div className="px-6 py-4 bg-white dark:bg-slate-800/80 rounded-2xl border border-amber-100 dark:border-amber-900/30 shadow-sm shadow-amber-500/5 flex items-center gap-4 group hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            <div className="relative p-3 bg-amber-50 dark:bg-amber-500/10 rounded-xl text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform duration-300">
                                <UserX size={24} strokeWidth={2.5} />
                                {pendingCount > 0 && (
                                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500 border-2 border-white dark:border-slate-800"></span>
                                    </span>
                                )}
                            </div>
                            <div className="relative">
                                <p className="text-xs font-bold text-amber-600/70 dark:text-amber-500/70 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                                    Pending Approvals
                                </p>
                                <p className="text-2xl font-black text-amber-700 dark:text-amber-400 leading-none">
                                    {pendingCount}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sleek Controls */}
            <div className="relative z-20 mb-6 flex flex-wrap gap-4 items-center justify-between">
                <div className="relative w-full max-w-lg group">
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-rose-500 rounded-2xl blur opacity-0 group-focus-within:opacity-20 transition-opacity duration-500" />
                    <div className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/50 rounded-2xl flex items-center px-4 py-3 shadow-sm">
                        <Search className="text-slate-400 mr-3" size={20} />
                        <input
                            type="text"
                            placeholder="Search users by name or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-transparent border-none outline-none text-slate-800 dark:text-slate-200 font-medium placeholder:text-slate-400"
                        />
                    </div>
                </div>
            </div>

            {/* Ultra-Premium Floating Users Table */}
            <div className="relative mt-12 mb-20">
                {/* Background ambient lighting for table area */}
                <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/5 via-rose-500/5 to-transparent blur-3xl pointer-events-none -z-10" />
                
                <div className="overflow-x-auto px-1 pb-10">
                    <table className="w-full border-separate border-spacing-y-4">
                        <thead>
                            <tr>
                                <th className="px-8 py-2 text-left text-[10px] font-black text-slate-400/80 dark:text-slate-500/80 uppercase tracking-[0.3em]">User Profile</th>
                                <th className="px-6 py-2 text-left text-[10px] font-black text-slate-400/80 dark:text-slate-500/80 uppercase tracking-[0.3em]">Role</th>
                                <th className="px-6 py-2 text-left text-[10px] font-black text-slate-400/80 dark:text-slate-500/80 uppercase tracking-[0.3em]">Status</th>
                                <th className="px-6 py-2 text-left text-[10px] font-black text-slate-400/80 dark:text-slate-500/80 uppercase tracking-[0.3em]">Joined Date</th>
                                <th className="px-8 py-2 text-right text-[10px] font-black text-slate-400/80 dark:text-slate-500/80 uppercase tracking-[0.3em]">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-8 py-20 text-center bg-white/40 dark:bg-slate-900/40 backdrop-blur-md rounded-[32px] border border-white/50 dark:border-slate-800 shadow-xl shadow-slate-200/20 dark:shadow-black/20">
                                        <div className="flex flex-col items-center justify-center text-slate-400">
                                            <Search size={48} className="mb-4 opacity-20" />
                                            <p className="text-lg font-bold">No users found</p>
                                            <p className="text-sm font-medium opacity-70">Try a different search term</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map((u, i) => (
                                    <tr
                                        key={u.uid}
                                        className="group relative bg-white/60 dark:bg-slate-900/60 backdrop-blur-xl hover:bg-white/90 dark:hover:bg-slate-800/90 shadow-lg shadow-slate-200/20 dark:shadow-black/20 hover:shadow-2xl hover:shadow-indigo-500/10 dark:hover:shadow-indigo-500/10 transition-all duration-500 hover:-translate-y-1 animate-in fade-in slide-in-from-bottom-4"
                                        style={{ animationFillMode: 'both', animationDelay: `${i * 50}ms` }}
                                    >
                                        <td className="px-8 py-5 border-y border-l border-white/50 dark:border-slate-700/50 group-hover:border-indigo-500/30 rounded-l-[24px] transition-colors duration-500 relative overflow-hidden">
                                            <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-indigo-500 to-rose-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                            <div className="flex items-center gap-5 relative z-10">
                                                <div className="relative">
                                                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 p-0.5 shadow-sm group-hover:shadow-md transition-all duration-300 group-hover:scale-105">
                                                        <div className="w-full h-full rounded-[14px] bg-white dark:bg-slate-900 flex items-center justify-center overflow-hidden">
                                                            {u.photoURL ? (
                                                                <img
                                                                    src={u.photoURL}
                                                                    alt={u.displayName || 'User'}
                                                                    className="w-full h-full object-cover"
                                                                    referrerPolicy="no-referrer"
                                                                    onError={(e) => {
                                                                        ;(e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(u.displayName || 'User')}&background=random`
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span className="text-lg font-black text-slate-400 bg-clip-text text-transparent bg-gradient-to-br from-slate-400 to-slate-600">
                                                                    {(u.displayName || u.email || 'U').charAt(0).toUpperCase()}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div
                                                        className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-[3px] border-white dark:border-slate-900 shadow-sm
                                                        ${
                                                            u.status === UserStatus.ACTIVE
                                                                ? 'bg-emerald-500'
                                                                : u.status === UserStatus.PENDING
                                                                  ? 'bg-amber-500'
                                                                  : 'bg-rose-500'
                                                        }`}
                                                    />
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-slate-800 dark:text-slate-100 text-base leading-tight mb-1 flex items-center gap-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                                        <span className="truncate max-w-[200px]">
                                                            {u.displayName || 'Unknown User'}
                                                        </span>
                                                        {(() => {
                                                            if (!u.lastLogin?.seconds) {
                                                                return (
                                                                    <span className="flex items-center gap-1.5 bg-slate-500/10 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-full text-[9px] uppercase font-black tracking-widest whitespace-nowrap">
                                                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-400/50" />
                                                                        Offline
                                                                    </span>
                                                                )
                                                            }

                                                            const diffInSeconds = Math.floor(Date.now() / 1000) - u.lastLogin.seconds
                                                            
                                                            // Heartbeat is every 5 minutes. Give a 2 min buffer (Total 420s = 7m)
                                                            if (diffInSeconds < 420) {
                                                                return (
                                                                    <span className="flex items-center gap-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full text-[9px] uppercase font-black tracking-widest whitespace-nowrap">
                                                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
                                                                        Online
                                                                    </span>
                                                                )
                                                            }

                                                            // Calculate Away time (MS Teams Style)
                                                            const minutes = Math.floor(diffInSeconds / 60)
                                                            const hours = Math.floor(minutes / 60)
                                                            const days = Math.floor(hours / 24)

                                                            let awayText = ''
                                                            if (days > 0) {
                                                                const remHours = hours % 24
                                                                const remMins = minutes % 60
                                                                awayText = `${days}d ${remHours > 0 ? `${remHours}h ` : ''}${remMins > 0 ? `${remMins}m` : ''}`
                                                            } else if (hours > 0) {
                                                                const remMins = minutes % 60
                                                                awayText = `${hours}h ${remMins > 0 ? `${remMins}m` : ''}`
                                                            } else {
                                                                awayText = `${minutes}m`
                                                            }

                                                            awayText = awayText.replace(/\s+/g, ' ').trim()

                                                            // If away for more than 7 days, just show Offline
                                                            if (days > 7) {
                                                                return (
                                                                    <span className="flex items-center gap-1.5 bg-slate-500/10 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-full text-[9px] uppercase font-black tracking-widest whitespace-nowrap">
                                                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-400/50" />
                                                                        Offline
                                                                    </span>
                                                                )
                                                            }

                                                            return (
                                                                <span className="flex items-center gap-1.5 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded-full text-[9px] uppercase font-black tracking-widest whitespace-nowrap">
                                                                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500/80" />
                                                                    Away {awayText} ago
                                                                </span>
                                                            )
                                                        })()}
                                                    </h3>
                                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                                        {u.email}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 border-y border-white/50 dark:border-slate-700/50 group-hover:border-indigo-500/30 transition-colors duration-500">
                                            <span
                                                className={`inline-flex items-center px-3 py-1.5 rounded-xl text-[11px] font-black uppercase tracking-widest
                                                ${
                                                    u.role === UserRole.ADMIN
                                                        ? 'bg-gradient-to-r from-purple-500/10 to-indigo-500/10 text-purple-700 dark:text-purple-300 border border-purple-200/50 dark:border-purple-800/50'
                                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                                                }`}
                                            >
                                                {u.role}
                                            </span>
                                        </td>
                                        <td className="px-6 py-5 border-y border-white/50 dark:border-slate-700/50 group-hover:border-indigo-500/30 transition-colors duration-500">
                                            <div
                                                className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest border 
                                                ${
                                                    u.status === UserStatus.ACTIVE
                                                        ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200/50 dark:border-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.1)]'
                                                        : u.status === UserStatus.PENDING
                                                          ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200/50 dark:border-amber-500/20 shadow-[0_0_15px_rgba(245,158,11,0.1)]'
                                                          : 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200/50 dark:border-rose-500/20'
                                                }`}
                                            >
                                                <span
                                                    className={`w-2 h-2 rounded-full shadow-sm ${
                                                        u.status === UserStatus.ACTIVE
                                                            ? 'bg-emerald-500 shadow-emerald-500/50'
                                                            : u.status === UserStatus.PENDING
                                                              ? 'bg-amber-500 shadow-amber-500/50'
                                                              : 'bg-rose-500 shadow-rose-500/50'
                                                    }`}
                                                />
                                                {u.status}
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 border-y border-white/50 dark:border-slate-700/50 group-hover:border-indigo-500/30 transition-colors duration-500 text-sm font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap">
                                            {u.createdAt?.seconds
                                                ? new Date(u.createdAt.seconds * 1000).toLocaleDateString(undefined, {
                                                      year: 'numeric',
                                                      month: 'long',
                                                      day: 'numeric',
                                                  })
                                                : '—'}
                                        </td>
                                        <td className="px-8 py-5 border-y border-r border-white/50 dark:border-slate-700/50 group-hover:border-indigo-500/30 rounded-r-[24px] transition-colors duration-500 text-right">
                                            <div className="flex items-center justify-end gap-2 opacity-80 group-hover:opacity-100 transition-opacity">
                                                {u.status === UserStatus.PENDING && (
                                                    <>
                                                        <button
                                                            onClick={() => {
                                                                setSelectedUser(u)
                                                                setActionType('approve')
                                                                setIsActionModalOpen(true)
                                                            }}
                                                            className="flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500 hover:text-white transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:-translate-y-0.5 active:scale-95"
                                                            title="Approve User"
                                                        >
                                                            <CheckCircle size={20} strokeWidth={2.5} />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setSelectedUser(u)
                                                                setActionType('reject')
                                                                setIsActionModalOpen(true)
                                                            }}
                                                            className="flex items-center justify-center w-10 h-10 rounded-xl bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 hover:bg-rose-500 hover:text-white transition-all duration-300 hover:shadow-[0_0_20px_rgba(244,63,94,0.4)] hover:-translate-y-0.5 active:scale-95"
                                                            title="Reject User"
                                                        >
                                                            <XCircle size={20} strokeWidth={2.5} />
                                                        </button>
                                                    </>
                                                )}
                                                {u.status === UserStatus.ACTIVE && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedUser(u)
                                                            setActionType('reject')
                                                            setIsActionModalOpen(true)
                                                        }}
                                                        className="px-4 py-2 rounded-xl bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-bold hover:bg-rose-500 hover:text-white hover:border-transparent transition-all duration-300 border border-slate-200 dark:border-slate-700 hover:shadow-[0_0_20px_rgba(244,63,94,0.4)] hover:-translate-y-0.5 active:scale-95"
                                                    >
                                                        Revoke
                                                    </button>
                                                )}
                                                {u.status === UserStatus.REJECTED && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedUser(u)
                                                            setActionType('approve')
                                                            setIsActionModalOpen(true)
                                                        }}
                                                        className="px-4 py-2 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-bold hover:bg-emerald-500 hover:text-white transition-all duration-300 border border-emerald-200/50 dark:border-emerald-500/20 hover:border-transparent hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:-translate-y-0.5 active:scale-95"
                                                    >
                                                        Approve
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        setSelectedUser(u)
                                                        setActionType('delete')
                                                        setIsActionModalOpen(true)
                                                    }}
                                                    className="flex items-center justify-center w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500 hover:bg-rose-500 hover:text-white transition-all duration-300 hover:shadow-[0_0_20px_rgba(244,63,94,0.4)] hover:-translate-y-0.5 active:scale-95 ml-2"
                                                    title="Delete User"
                                                >
                                                    <Trash2 size={18} strokeWidth={2.5} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Action Modal */}
            <Modal isOpen={isActionModalOpen} onClose={() => setIsActionModalOpen(false)} title="User Action">
                <div className="p-6 text-center">
                    <div
                        className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 
                        ${actionType === 'delete' || actionType === 'reject' ? 'bg-rose-50 text-rose-500' : 'bg-emerald-50 text-emerald-500'}`}
                    >
                        {actionType === 'delete' && <Trash2 size={32} />}
                        {actionType === 'reject' && <UserX size={32} />}
                        {actionType === 'approve' && <CheckCircle size={32} />}
                    </div>

                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                        {actionType === 'approve' && 'Approve User?'}
                        {actionType === 'reject' && 'Reject User Access?'}
                        {actionType === 'delete' && 'Delete User Account?'}
                    </h3>

                    <p className="text-slate-500 text-sm mb-6 max-w-xs mx-auto">
                        Are you sure you want to {actionType}{' '}
                        <span className="font-bold text-slate-700">{selectedUser?.email}</span>?
                        {actionType === 'delete' && ' This action cannot be undone.'}
                    </p>

                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={() => setIsActionModalOpen(false)}
                            className="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-600 font-bold text-sm hover:bg-slate-200 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                if (selectedUser && actionType) {
                                    if (actionType === 'approve')
                                        handleUpdateStatus(selectedUser.uid, UserStatus.ACTIVE)
                                    if (actionType === 'reject')
                                        handleUpdateStatus(selectedUser.uid, UserStatus.REJECTED)
                                    if (actionType === 'delete') handleDeleteUser(selectedUser.uid)
                                }
                            }}
                            className={`px-5 py-2.5 rounded-xl font-bold text-sm text-white shadow-lg shadow-indigo-500/20 active:scale-95 transition-all
                                ${actionType === 'approve' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-rose-500 hover:bg-rose-600'}`}
                        >
                            Confirm {actionType}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    )
}
